/**
 * #933 — Owner Settings.
 *
 * The one place a tenant's business decisions become settings instead of
 * scripts. Tabs are URL-addressable (`/settings/operations`, and the
 * Capacity / Services tabs the epic adds next) so a link opens the right
 * card, and the tab strip is the same APG pattern Reports uses (roving
 * tabindex, Arrow/Home/End). Only shipped tabs render — a tab that leads
 * nowhere is a dead end (Principle 2).
 *
 * Sibling, not the same: #873 is the per-USER profile (text size,
 * password). This page is per-TENANT.
 */
import type { KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { PageHeader } from '@ferro/ui';
import { readSession } from '../lib/api.js';
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';
import { useManagerSettingsConfirmed } from '../lib/settings-access.js';
import { PageErrorBoundary } from '../components/PageErrorBoundary.js';
import { OperationsSettingsCard } from '../components/settings/OperationsSettingsCard.js';
// Sawyer 10 customer-booking-verify-policy (C5 C6) — Care details at booking.
import { BookingCareSettingsCard } from '../components/settings/BookingCareSettingsCard.js';
import { CapacityRulesCard } from '../components/settings/CapacityRulesCard.js';
// Sawyer 12 incident-wording-pet-notes (F3)
import { WordingSettingsCard } from '../components/settings/WordingSettingsCard.js';
import { BlockedDaysCard } from '../components/settings/BlockedDaysCard.js';
import { AssessmentScheduleCard } from '../components/settings/AssessmentScheduleCard.js';
// Sawyer 09 pet-inactivity (D1)
import { PetInactivityCard } from '../components/settings/PetInactivityCard.js';
import { ServicesSettingsCard } from '../components/settings/ServicesSettingsCard.js';
import { LodgingSettingsCard } from '../components/settings/LodgingSettingsCard.js';
import { AgreementsSettingsCard } from '../components/settings/AgreementsSettingsCard.js';
import { MessagingSettingsCard } from '../components/settings/MessagingSettingsCard.js';
import { TextingMessagesCard } from '../components/settings/TextingMessagesCard.js';
import { AssistantSettingsCard } from '../components/settings/AssistantSettingsCard.js';
import { ReferralShareSettingsCard } from '../components/settings/ReferralShareSettingsCard.js';

export type SettingsSection =
  | 'operations'
  | 'capacity'
  | 'services'
  | 'lodging'
  | 'agreements'
  | 'rewards'
  | 'messaging'
  | 'assistant';

export const SETTINGS_TABS: ReadonlyArray<{
  key: SettingsSection;
  label: string;
  to: string;
}> = [
  { key: 'operations', label: 'Operations', to: '/settings/operations' },
  { key: 'capacity', label: 'Capacity & assessments', to: '/settings/capacity' },
  { key: 'services', label: 'Services & prices', to: '/settings/services' },
  // #939 3a — beds and the lodging threshold.
  { key: 'lodging', label: 'Lodging', to: '/settings/lodging' },
  // #1114 — the documents customers sign, per document, with the re-sign switch.
  { key: 'agreements', label: 'Agreements', to: '/settings/agreements' },
  // What a client sends when they refer a friend. Point value stays on Loyalty (#524).
  { key: 'rewards', label: 'Rewards', to: '/settings/rewards' },
  // #1212 — texting B1: the business's own Twilio account, and what clients
  // get when they text. Shown to whoever can read the business's settings
  // (see visibleTabs); the Twilio card itself stays owner-only.
  { key: 'messaging', label: 'Texting', to: '/settings/messaging' },
  // Woofers Phase 0 — the assistant's name and the per-surface AI switches.
  { key: 'assistant', label: 'AI Assistant', to: '/settings/assistant' },
];

/** Tabs gated by a permission of their own; a tab that would only 403 is a dead end (Principle 2). */
const TAB_PERMISSION: Partial<Record<SettingsSection, string>> = {
  messaging: 'business-config:read:all',
};

interface SettingsProps {
  config: ClientConfig;
  section: SettingsSection;
}

export function Settings({ config, section }: SettingsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const navigate = useNavigate();
  const roles = effectiveRolesOf(readSession().staff);
  // Prices (and adding a service, which sets one) are their own permission —
  // the same split the server's route map documents (PR 3). The charging
  // fields on the Operations card follow it (the server's access.ts).
  const canEditPrice = rolesHavePermission(roles, 'business-config.pricing:write:all');
  // #933 D2 — the general write is owner + manager. The owner has it outright;
  // a manager has it once the server says so (lib/settings-access.ts).
  const holdsSettingsWrite = rolesHavePermission(roles, 'business-config:write:all');
  const managerConfirmed = useManagerSettingsConfirmed(apiUrl, holdsSettingsWrite && !canEditPrice);
  const canEdit = holdsSettingsWrite && (canEditPrice || managerConfirmed);
  // The AI on/off switches stay with the owner; the assistant's name does not.
  const canEditAiSwitches = rolesHavePermission(roles, 'messaging.ai-config:write:all');
  // Sawyer 04 manager-beds (B3) — beds are their own permission (owner + manager); the
  // threshold on the same card is the general write.
  const canEditBeds = rolesHavePermission(roles, 'business-config.lodging:write:all');
  // SPEC 2026-10-26 D1 — the required-vaccine list is its own permission (owner + manager);
  // the off/warn/block gate and the vaccination toggles beside it are the general write.
  const canEditVaccinations = canEdit || rolesHavePermission(roles, 'business-config.vaccinations:write:all');
  // #1114 — agreements are customer contracts: read for owner/manager/receptionist, write for owner/manager.
  const canReadAgreements = rolesHavePermission(roles, 'contracts.customer:read:all');
  const canEditAgreements = rolesHavePermission(roles, 'contracts.customer:write:all');
  // #1212 — the gateway-credentials gate (owner): a third-party account secret.
  const canManageMessaging = rolesHavePermission(roles, 'payments-ops.credentials:write:all');
  const visibleTabs = SETTINGS_TABS.filter((t) => {
    const needs = TAB_PERMISSION[t.key];
    return needs === undefined || rolesHavePermission(roles, needs);
  });

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, current: SettingsSection) => {
    const idx = visibleTabs.findIndex((t) => t.key === current);
    let next = idx;
    if (event.key === 'ArrowRight') next = (idx + 1) % visibleTabs.length;
    else if (event.key === 'ArrowLeft') next = (idx - 1 + visibleTabs.length) % visibleTabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = visibleTabs.length - 1;
    else return;
    event.preventDefault();
    const tab = visibleTabs[next];
    if (!tab) return;
    navigate(tab.to);
    document.getElementById(`settings-tab-${tab.key}`)?.focus();
  };

  return (
    <PageErrorBoundary>
      <div className="ferro-page ferro-settings" data-testid="settings-page">
        <PageHeader
          title="Settings"
          subtitle={
            canEdit && canEditPrice
              ? 'How your facility runs. Change a number here and the next booking honors it.'
              : canEdit
                ? 'How your facility runs. Change a number here and the next booking honors it. Prices, charges and AI switches stay with the business owners.'
                : section === 'lodging' && canEditBeds
                  ? 'How your facility runs. You can change the beds here; business owners change everything else.'
                  : section === 'operations' && canEditVaccinations
                    ? 'How your facility runs. You can change the required vaccinations here; business owners change everything else.'
                    : 'How your facility runs. Owners and managers change these; you can see them.'
          }
        />
        <div className="ferro-tabs ferro-settings__tabs" role="tablist" aria-label="Settings section">
          {visibleTabs.map((t) => (
            <button
              key={t.key}
              id={`settings-tab-${t.key}`}
              type="button"
              role="tab"
              aria-selected={section === t.key}
              aria-controls={`settings-panel-${t.key}`}
              tabIndex={section === t.key ? 0 : -1}
              className={`ferro-tabs__tab${section === t.key ? ' ferro-tabs__tab--active' : ''}`}
              onClick={() => navigate(t.to)}
              onKeyDown={(e) => handleTabKeyDown(e, t.key)}
              data-testid={`settings-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {section === 'operations' ? (
          <section
            id="settings-panel-operations"
            role="tabpanel"
            aria-labelledby="settings-tab-operations"
            tabIndex={0}
            className="ferro-settings__panel ferro-settings__stack"
          >
            {/* Sawyer 12 incident-wording-pet-notes (F3) */}
            <WordingSettingsCard apiUrl={apiUrl} canEdit={canEdit} />
            <OperationsSettingsCard
              apiUrl={apiUrl}
              canEdit={canEdit}
              canEditCharges={canEditPrice}
              canEditVaccinations={canEditVaccinations}
            />
            {/* Sawyer 10 customer-booking-verify-policy (C5 C6) — renders nothing on an older server (403 route_not_mapped). */}
            <BookingCareSettingsCard apiUrl={apiUrl} canEdit={canEdit} />
          </section>
        ) : null}

        {section === 'capacity' ? (
          <section
            id="settings-panel-capacity"
            role="tabpanel"
            aria-labelledby="settings-tab-capacity"
            tabIndex={0}
            className="ferro-settings__panel ferro-settings__stack"
          >
            {/* PR 2b — three cards over the PR 2a routes. Order = how the
                desk thinks about a week: the everyday limits, the exceptions,
                then the one service with its own calendar. */}
            <CapacityRulesCard apiUrl={apiUrl} canEdit={canEdit} />
            <BlockedDaysCard apiUrl={apiUrl} canEdit={canEdit} />
            <AssessmentScheduleCard apiUrl={apiUrl} canEdit={canEdit} canEditPrice={canEditPrice} />
            {/* Sawyer 09 pet-inactivity (D1) — renders nothing on an older server. */}
            <PetInactivityCard apiUrl={apiUrl} canEdit={canEdit} />
          </section>
        ) : null}

        {section === 'services' ? (
          <section
            id="settings-panel-services"
            role="tabpanel"
            aria-labelledby="settings-tab-services"
            tabIndex={0}
            className="ferro-settings__panel"
          >
            <ServicesSettingsCard apiUrl={apiUrl} canEdit={canEdit} canEditPrice={canEditPrice} />
          </section>
        ) : null}

        {section === 'lodging' ? (
          <section
            id="settings-panel-lodging"
            role="tabpanel"
            aria-labelledby="settings-tab-lodging"
            tabIndex={0}
            className="ferro-settings__panel"
          >
            <LodgingSettingsCard apiUrl={apiUrl} canEdit={canEditBeds} canEditThreshold={canEdit} />
          </section>
        ) : null}

        {section === 'agreements' ? (
          <section
            id="settings-panel-agreements"
            role="tabpanel"
            aria-labelledby="settings-tab-agreements"
            tabIndex={0}
            className="ferro-settings__panel"
          >
            <AgreementsSettingsCard apiUrl={apiUrl} canRead={canReadAgreements} canEdit={canEditAgreements} />
          </section>
        ) : null}

        {section === 'rewards' ? (
          <section
            id="settings-panel-rewards"
            role="tabpanel"
            aria-labelledby="settings-tab-rewards"
            tabIndex={0}
            className="ferro-settings__panel ferro-settings__stack"
          >
            {/* Renders nothing on an older server (403 route_not_mapped). */}
            <ReferralShareSettingsCard apiUrl={apiUrl} canEdit={canEdit} />
            {canEdit ? (
              <p className="ferro-settings__teaching ferro-referral-share__more" data-testid="settings-rewards-point-value">
                Point value and reward tiers are on the <Link to="/loyalty">Loyalty page</Link>.
              </p>
            ) : null}
          </section>
        ) : null}

        {section === 'messaging' ? (
          <section
            id="settings-panel-messaging"
            role="tabpanel"
            aria-labelledby="settings-tab-messaging"
            tabIndex={0}
            className="ferro-settings__panel"
          >
            <TextingMessagesCard apiUrl={apiUrl} canEdit={canEdit} canManageTwilio={canManageMessaging} />
            <MessagingSettingsCard apiUrl={apiUrl} canManage={canManageMessaging} />
          </section>
        ) : null}

        {section === 'assistant' ? (
          <section
            id="settings-panel-assistant"
            role="tabpanel"
            aria-labelledby="settings-tab-assistant"
            tabIndex={0}
            className="ferro-settings__panel"
          >
            <AssistantSettingsCard apiUrl={apiUrl} canEdit={canEdit} canEditSwitches={canEditAiSwitches} />
          </section>
        ) : null}
      </div>
    </PageErrorBoundary>
  );
}
